import { LoadState, useLoad } from "@redotech/react-util/load";
import { StyleRoot } from "@redotech/react-util/style";
import { MarketingEntryPoint } from "@redotech/redo-model/advanced-flow/schemas/marketing/marketing";
import { getSignupFormSchema } from "@redotech/redo-model/marketing/schemas/get-signup-form-schema";
import { submitSignupFormStepSchema as submitSignupFormStep } from "@redotech/redo-model/marketing/schemas/submit-signup-form-step-schema";
import { logSignupFormAnalyticsSchema as logSignupFormAnalytics } from "@redotech/redo-model/marketing/schemas/view-signup-form-schema";
import { SignupForm as ISignupForm } from "@redotech/redo-model/signup-form-schema";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { CustomFieldProvider } from "@redotech/redo-web/marketing/form-elements/context";
import { SignupForm } from "@redotech/redo-web/marketing/signup-form";
import {
  SignupFormClient,
  SignupFormLocalData,
} from "@redotech/redo-web/marketing/signup-form/types";
import {
  parseLocalData,
  submitFormStep,
  useSignupFormAnalytics,
} from "@redotech/redo-web/marketing/signup-form/utils";
import { ANONYMOUS_SHOPPER_ID_KEY } from "@redotech/redo-web/utils/shared-conf";
import { RedoShopifyRpcClient } from "@redotech/shopify-sdk/rpc/client";
import { getCustomFieldOptions } from "@redotech/shopify-sdk/rpc/schema/marketing-embedded-form/get-custom-field-options";
import {
  getEmbeddedForms,
  GetEmbeddedFormsResponse,
  getEmbeddedFormsResponseSchema,
} from "@redotech/shopify-sdk/rpc/schema/marketing-embedded-form/get-embedded-forms";
import { useCallback, useContext, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  RedoShopifyRpcClientContext,
  RedoShopifyRpcClientContextProvider,
} from "../client/redo-shopify-rpc-client";
import { log } from "../monitoring/log";
import { RedoRoot } from "../root";
import STYLE_MANAGER from "../style-manager";

const SIGNUP_LOCAL_STORAGE_KEY = "redo.marketing-signup-form";

export function initialize(autocheck: WidgetAutocheck) {
  log("Initializing Marketing embedded form");

  const observer = new MutationObserver((mutationList) => {
    for (const mutation of mutationList) {
      for (const addedNode of mutation.addedNodes) {
        if (addedNode instanceof HTMLElement) {
          findAndPopulateFormContainers({
            parentElement: addedNode,
            autocheck,
          });
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  findAndPopulateFormContainers({ parentElement: document.body, autocheck });
}

function findAndPopulateFormContainers({
  autocheck,
  parentElement,
}: {
  autocheck: WidgetAutocheck;
  parentElement: HTMLElement;
}) {
  const elements = parentElement.querySelectorAll(
    'div[id^="redo-marketing-embedded-form-"]:not([data-initialized="true"])',
  );

  const matchingElements = Array.from(elements).filter((element) => {
    return /^[a-zA-Z0-9]{5}$/.test(element.id.split("-").pop() ?? "");
  });

  if (matchingElements.length === 0) {
    return;
  }

  log(`Found ${matchingElements.length} Marketing embedded form containers`);

  for (const element of matchingElements) {
    populateFormContainer({ autocheck, formDiv: element as HTMLElement });
  }
}

function populateFormContainer({
  autocheck,
  formDiv,
}: {
  autocheck: WidgetAutocheck;
  formDiv: HTMLElement;
}) {
  // Shopify applies display: none to empty divs
  formDiv.style.display = "unset";
  formDiv.dataset.initialized = "true";

  const root = createRoot(formDiv);
  root.render(
    <StyleRoot container={formDiv} styleManager={STYLE_MANAGER}>
      <RedoRoot
        extensionShopifyClient={undefined}
        storeUrl={
          globalThis.redoStorefront?.storeUrl ??
          globalThis.Shopify?.shop ??
          globalThis.Shopify?.Checkout?.apiHost
        }
      >
        <RedoShopifyRpcClientContextProvider
          baseUrl={
            globalThis.redoStorefront?.env?.REDO_SHOPIFY_SERVER_URL ??
            process.env.REDO_SHOPIFY_SERVER_URL!
          }
          storeUrl={
            globalThis.redoStorefront?.storeUrl ??
            globalThis.Shopify?.shop ??
            globalThis.Shopify?.Checkout?.apiHost
          }
          widgetId={globalThis.redoWidgetId}
        >
          <RedoMarketingEmbeddedForm
            autocheck={autocheck}
            formDivId={formDiv.id}
          />
        </RedoShopifyRpcClientContextProvider>
      </RedoRoot>
    </StyleRoot>,
  );
}

function useFormData(
  formsLoad: LoadState<any[]>,
  rpcClient: RedoShopifyRpcClient | undefined,
  shopifyCustomerId: string | null,
  formDivId: string,
): {
  form: ISignupForm | null;
  formId: string | null;
  merchantPhoneNumber: string | undefined;
} {
  const [form, setForm] = useState<ISignupForm | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [merchantPhoneNumber, setMerchantPhoneNumber] = useState<
    string | undefined
  >(undefined);

  if (formsLoad.value && rpcClient && !form) {
    const matchingForm = formsLoad.value.find(
      (f: (typeof formsLoad.value)[number]) =>
        f.form?.behavior.embeddedCode.includes(formDivId),
    );

    if (matchingForm) {
      log("Form found", matchingForm);
      const anonymousId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);

      if (anonymousId) {
        void rpcClient(logSignupFormAnalytics, {
          anonymousId,
          shopifyCustomerId,
          formId: matchingForm.id,
          type: "view",
          treatmentId: null,
        }).catch((error: any) => {
          console.error("Error logging form view:", error);
        });
      }

      setForm(matchingForm.form);
      setFormId(matchingForm.id);
      setMerchantPhoneNumber(matchingForm.merchantPhoneNumber);
    }
  }

  return { form, formId, merchantPhoneNumber };
}

function RedoMarketingEmbeddedForm({
  autocheck,
  formDivId,
}: {
  autocheck: WidgetAutocheck;
  formDivId: string;
}) {
  const rpcClient = useContext(RedoShopifyRpcClientContext);
  const [currentStep, setCurrentStep] = useState(0);
  const [localData, setLocalData] = useState<SignupFormLocalData>(() => {
    const stored = localStorage.getItem(SIGNUP_LOCAL_STORAGE_KEY);
    return stored ? parseLocalData(stored) : {};
  });

  const shopifyCustomerId = useMemo(
    () =>
      (
        globalThis as any
      ).ShopifyAnalytics?.meta?.page?.customerId?.toString() ?? null,
    [],
  );

  const formsLoad = useLoad<GetEmbeddedFormsResponse>(async () => {
    if (!rpcClient) {
      return [];
    }

    if (globalThis.redoStorefront?.marketing?.embeddedForms) {
      const formsParseResult = getEmbeddedFormsResponseSchema.safeParse(
        globalThis.redoStorefront.marketing.embeddedForms,
      );
      if (formsParseResult.success) {
        return formsParseResult.data;
      }
    }

    const forms = await rpcClient(getEmbeddedForms, {});
    globalThis.redoStorefront = {
      ...globalThis.redoStorefront!,
      marketing: {
        ...globalThis.redoStorefront?.marketing,
        embeddedForms: forms,
      },
    };

    return forms.filter((f: (typeof forms)[number]) => f.publishedAt);
  }, []);

  const { form, formId, merchantPhoneNumber } = useFormData(
    formsLoad,
    rpcClient,
    shopifyCustomerId,
    formDivId,
  );

  const signupFormClient: SignupFormClient | undefined = useMemo(() => {
    if (!rpcClient) {
      return;
    }
    return {
      getSignupForm: async (params, options) =>
        rpcClient(getSignupFormSchema, params, options),
      getCustomFieldOptions: async (params) =>
        rpcClient(getCustomFieldOptions, params),
      logSignupFormAnalytics: async (params) =>
        rpcClient(logSignupFormAnalytics, params),
      submitSignupFormStep: async (params) =>
        rpcClient(submitSignupFormStep, params),
    } satisfies SignupFormClient;
  }, [rpcClient]);

  const { logAnalytics } = useSignupFormAnalytics({
    client: signupFormClient,
    formId,
    treatmentId: null,
    shopifyCustomerId,
    localData,
    setLocalData,
  });

  const submitStep = useCallback(
    async (i: number, formData: Record<string, string>) => {
      await submitFormStep({
        i,
        formData,
        form,
        formId,
        client: signupFormClient,
        localData,
        logAnalytics,
        setCurrentStep,
        entryPoint: MarketingEntryPoint.SIGNUP_FORM,
        skipPhoneSteps: true,
        treatmentId: null,
      });
    },
    [signupFormClient, localData, form, formId, logAnalytics],
  );

  if (formsLoad.pending) {
    return null;
  }

  if (formsLoad.error) {
    return null;
  }

  if (!form) {
    return null;
  }

  if (rpcClient) {
    return (
      <CustomFieldProvider
        getCustomFieldOptions={(id) =>
          rpcClient(getCustomFieldOptions, { customFieldId: id })
        }
      >
        <SignupForm
          brandKit={autocheck.brandKit}
          currentStep={currentStep}
          form={form}
          merchantPhoneNumber={merchantPhoneNumber}
          onSubmitStep={submitStep}
        />
      </CustomFieldProvider>
    );
  }

  return (
    <SignupForm
      brandKit={autocheck.brandKit}
      currentStep={currentStep}
      form={form}
      merchantPhoneNumber={merchantPhoneNumber}
      onSubmitStep={submitStep}
    />
  );
}
